import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import Logo from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

export default function AdminLoginPage() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-ink">Laden…</div>
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/admin");
    else setError(res.error || "Inloggen mislukt");
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <div className="container-x pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-ink transition" data-testid="admin-back-home">
          <ArrowLeft size={16} /> Terug naar website
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="mx-auto mb-5 flex justify-center">
              <Logo height={64} />
            </div>
            <h1 className="font-display text-3xl text-ink tracking-tight">Beheer</h1>
            <p className="text-sm text-ink/60 mt-2">Stichting <em className="italic">FIN</em> — inloggen</p>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-white border border-ink/10 rounded-2xl p-7 shadow-[0_8px_30px_rgba(15,27,45,0.04)]"
            data-testid="admin-login-form"
          >
            <label htmlFor="email" className="text-[13px] font-bold text-ink mb-2 block">E-mail</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-cream border border-ink/15 rounded-xl px-4 py-3 text-[15px] focus:ring-2 focus:ring-ink focus:border-transparent transition mb-4"
              data-testid="admin-email-input"
            />

            <label htmlFor="password" className="text-[13px] font-bold text-ink mb-2 block">Wachtwoord</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-cream border border-ink/15 rounded-xl px-4 py-3 text-[15px] focus:ring-2 focus:ring-ink focus:border-transparent transition"
              data-testid="admin-password-input"
            />

            {error && (
              <p className="mt-4 text-sm text-red-700 flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="admin-login-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full bg-ink text-cream hover:bg-ink2 active:scale-[0.98] disabled:opacity-60 transition rounded-xl py-3 font-semibold"
              data-testid="admin-login-submit"
            >
              {submitting ? "Inloggen…" : "Inloggen"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
